// src/pages/WorkingCapitalPage.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function WorkingCapitalPage() {
  const { projectId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Local copy of workingCapitalRequirement so we can read "days" for calculation
  const [wcrItems, setWcrItems] = useState([]);

  // 1) Load all inputs from API
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setData(data);
        setWcrItems(
          Array.isArray(data.workingCapitalRequirement)
            ? data.workingCapitalRequirement.map((r) => ({ ...r }))
            : []
        );
      })
      .catch(() => setError("Failed to load working-capital data."))
      .finally(() => setLoading(false));
  }, [projectId]);

  // ────────────────────────────────────────────────────────────
  // 2) parse mm-yyyy into {month, year}, or null
  // ────────────────────────────────────────────────────────────
  const prod = useMemo(() => {
    if (!data?.termLoanInput?.startMonth) return null;
    const [m, y] = data.termLoanInput.startMonth.split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [data]);

  // 3) months active in a FY label "YYYY-YY"
  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prod) return 12;
      const fyStart = parseInt(fyLabel, 10);
      if (isNaN(fyStart)) return 12;
      const fyEnd = fyStart + 1;
      // months from prod until 31-Mar of fyEnd
      const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prod]
  );

  // helper: safe getter
  const G = (path, def = []) =>
    path.split(".").reduce((o, k) => o?.[k] ?? null, data) ?? def;

  // once data is loaded:
  const {
    projectYears = [],
    manufacturedProducts = [],
    productWiseRawMaterialCostRs = {},
    rmConsumptionInputs = [],
    totalCostOfRawMaterial: totalCostRawFromApi = [],
    averageCostOfProdPerMT: avgCostProdFromApi = [],
    totalAnnualSales: totalAnnualSalesFromApi = [],
    totalClosingStock: totalClosingStockFromApi = [],
    // NEW: existing saved values from server for safety fallback
    rawMaterials: rawMaterialsFromApi = [],
    finishedGoods: finishedGoodsFromApi = [],
  } = data || {};

  // ────────────────────────────────────────────────────────────
  // 4) LocalStorage fallback for Annual Sales & Closing Stock
  // (because AnnualSalesRealisation writes them to localStorage)
  // ────────────────────────────────────────────────────────────
  const totalAnnualSales = useMemo(() => {
    if (
      Array.isArray(totalAnnualSalesFromApi) &&
      totalAnnualSalesFromApi.length
    )
      return totalAnnualSalesFromApi;

    if (typeof window === "undefined") return totalAnnualSalesFromApi || [];
    try {
      const raw = window.localStorage.getItem("totalAnnualSales");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return totalAnnualSalesFromApi || [];
    }
  }, [totalAnnualSalesFromApi]);

  const totalClosingStock = useMemo(() => {
    if (
      Array.isArray(totalClosingStockFromApi) &&
      totalClosingStockFromApi.length
    )
      return totalClosingStockFromApi;

    if (typeof window === "undefined") return totalClosingStockFromApi || [];
    try {
      const raw = window.localStorage.getItem("totalClosingStock");
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return totalClosingStockFromApi || [];
    }
  }, [totalClosingStockFromApi]);

  const totalCostRaw = totalCostRawFromApi || [];
  const avgCostProd = avgCostProdFromApi || [];

  // 5) avg working days from RM inputs
  const avgWorkingDays = useMemo(() => {
    const arr = rmConsumptionInputs || [];
    if (!arr.length) return 0;
    return (
      arr.reduce((s, r) => s + (Number(r.workingDays) || 0), 0) / arr.length
    );
  }, [rmConsumptionInputs]);

  // 6) raw-material total cost A per FY (from productWiseRawMaterialCostRs)
  const rmTotals = useMemo(() => {
    return projectYears.map((yr) =>
      (manufacturedProducts || []).reduce((sum, p) => {
        const name = typeof p === "string" ? p : p.product;
        return sum + (Number(productWiseRawMaterialCostRs?.[name]?.[yr]) || 0);
      }, 0)
    );
  }, [projectYears, manufacturedProducts, productWiseRawMaterialCostRs]);

  // 7) days for RM, Debtors & Creditors (from wcrItems)
  const daysFor = useMemo(() => {
    const obj = {};
    (wcrItems || []).forEach((r) => {
      obj[r.particular] = Number(r.days) || 0;
    });
    return obj;
  }, [wcrItems]);

  // ────────────────────────────────────────────────────────────
  // 8) generic value calculator pro-rated to FY months
  //  → with safety fallback so values don't randomly become 0
  //  → FIRST YEAR ALWAYS 0 (calculation starts from second year)
  // ────────────────────────────────────────────────────────────
  const computeValue = useCallback(
    (particular, days, yi) => {
      if (!projectYears.length) return 0;

      // ✅ Always exclude the first year
      if (yi === 0) return 0;

      const months = getMonthsActive(projectYears[yi]);
      const proration = months / 12;

      switch (particular) {
        case "Raw Materials": {
          // original formula
          let value =
            avgWorkingDays && totalCostRaw[yi] != null
              ? (Number(totalCostRaw[yi]) / avgWorkingDays) * days
              : 0;

          // If this accidentally comes 0 (e.g. transient missing inputs),
          // use previously saved values instead of showing 0.
          if (!value && Array.isArray(rawMaterialsFromApi)) {
            const stored = Number(rawMaterialsFromApi[yi]) || 0;
            if (stored) value = stored;
          }

          if (!value && typeof window !== "undefined") {
            try {
              const raw = window.localStorage.getItem(
                "workingCapitalRawMaterials"
              );
              const parsed = raw ? JSON.parse(raw) : [];
              const stored = Number(parsed[yi]) || 0;
              if (stored) value = stored;
            } catch {
              // ignore localStorage errors
            }
          }

          return value;
        }

        case "Finished Goods & WIP": {
          // original formula
          const closing = Number(totalClosingStock[yi]) || 0;
          const avgCost = Number(avgCostProd[yi]) || 0;
          let value = (closing * avgCost) / 100000;

          // safety fallback to previously saved values
          if (!value && Array.isArray(finishedGoodsFromApi)) {
            const stored = Number(finishedGoodsFromApi[yi]) || 0;
            if (stored) value = stored;
          }

          if (!value && typeof window !== "undefined") {
            try {
              const raw = window.localStorage.getItem(
                "workingCapitalFinishedGoods"
              );
              const parsed = raw ? JSON.parse(raw) : [];
              const stored = Number(parsed[yi]) || 0;
              if (stored) value = stored;
            } catch {
              // ignore localStorage errors
            }
          }

          return value;
        }

        case "Sundry Debtors": {
          // (Total Annual Sales / avg working days) * days + 10, then prorated
          const sales = Number(totalAnnualSales[yi]) || 0;
          const base = avgWorkingDays ? (sales / avgWorkingDays) * days : 0;
          return (base + 10) * proration;
        }

        case "Sundry Creditors": {
          // purchases = raw-materials consumed this FY
          const rmDays = daysFor["Raw Materials"] || 0;
          const closing = computeValue("Raw Materials", rmDays, yi);
          const consumption = Number(rmTotals[yi]) || 0;
          const opening =
            yi > 0 ? computeValue("Raw Materials", rmDays, yi - 1) : 0;
          const purch = closing + consumption - opening;
          return (purch / 365) * days * proration;
        }

        default:
          return 0;
      }
    },
    [
      projectYears,
      getMonthsActive,
      avgWorkingDays,
      totalCostRaw,
      totalClosingStock,
      avgCostProd,
      totalAnnualSales,
      daysFor,
      rmTotals,
      rawMaterialsFromApi,
      finishedGoodsFromApi,
    ]
  );

  // ────────────────────────────────────────────────────────────
  // 9) Working-Capital Requirement per year
  //  → FIRST YEAR ALWAYS 0
  // ────────────────────────────────────────────────────────────
  const workingCapitalRequirementByYear = useMemo(() => {
    return projectYears.map((_, yi) => {
      if (yi === 0) return 0;

      const rmVal = computeValue("Raw Materials", daysFor["Raw Materials"], yi);
      const fgVal = computeValue(
        "Finished Goods & WIP",
        daysFor["Finished Goods & WIP"],
        yi
      );
      const debtors = computeValue(
        "Sundry Debtors",
        daysFor["Sundry Debtors"],
        yi
      );
      const totalFirst3 = rmVal + fgVal + debtors;

      // Creditors of RM from inline logic:
      const days = daysFor["Sundry Creditors"] || 0;
      const closing = rmVal;
      const consumption = Number(totalCostRaw[yi]) || 0;
      const opening =
        yi > 0
          ? computeValue("Raw Materials", daysFor["Raw Materials"], yi - 1)
          : 0;
      const netPurchases = closing + consumption - opening;
      const sundryCreditorValue = (netPurchases / 365) * days;

      // WCR
      return totalFirst3 - sundryCreditorValue;
    });
  }, [projectYears, computeValue, daysFor, totalCostRaw]);

  // ────────────────────────────────────────────────────────────
  // 10) Raw Materials & Finished Goods values per year
  // ────────────────────────────────────────────────────────────
  const rawMaterialsByYear = useMemo(
    () =>
      projectYears.map((_, yi) =>
        computeValue("Raw Materials", daysFor["Raw Materials"], yi)
      ),
    [projectYears, computeValue, daysFor]
  );

  const finishedGoodsByYear = useMemo(
    () =>
      projectYears.map((_, yi) =>
        computeValue(
          "Finished Goods & WIP",
          daysFor["Finished Goods & WIP"],
          yi
        )
      ),
    [projectYears, computeValue, daysFor]
  );

  // 11) Working Capital (Bank) per year
  const bankPct = Number(G("workingCapitalInputs.bankContribution", 0));

  const bankByYear = useMemo(() => {
    const firstPos = projectYears.findIndex((_, yi) => {
      const rmVal = computeValue("Raw Materials", daysFor["Raw Materials"], yi);
      const fgVal = computeValue(
        "Finished Goods & WIP",
        daysFor["Finished Goods & WIP"],
        yi
      );
      const debtors = computeValue(
        "Sundry Debtors",
        daysFor["Sundry Debtors"],
        yi
      );
      const total3 = rmVal + fgVal + debtors;

      const days = daysFor["Sundry Creditors"] || 0;
      const closing = rmVal;
      const consumption = Number(totalCostRaw[yi]) || 0;
      const opening =
        yi > 0
          ? computeValue("Raw Materials", daysFor["Raw Materials"], yi - 1)
          : 0;
      const netPurchases = closing + consumption - opening;
      const creditors = (netPurchases / 365) * days;
      const wcr = total3 - creditors;
      return wcr > 0;
    });

    let roundedBank = 0;
    if (firstPos >= 0) {
      const yi = firstPos;
      const rmVal = computeValue("Raw Materials", daysFor["Raw Materials"], yi);
      const fgVal = computeValue(
        "Finished Goods & WIP",
        daysFor["Finished Goods & WIP"],
        yi
      );
      const debtors = computeValue(
        "Sundry Debtors",
        daysFor["Sundry Debtors"],
        yi
      );
      const total3 = rmVal + fgVal + debtors;

      const days = daysFor["Sundry Creditors"] || 0;
      const closing = rmVal;
      const consumption = Number(totalCostRaw[yi]) || 0;
      const opening =
        yi > 0
          ? computeValue("Raw Materials", daysFor["Raw Materials"], yi - 1)
          : 0;
      const netPurchases = closing + consumption - opening;
      const creditors = (netPurchases / 365) * days;
      const wcr = total3 - creditors;

      roundedBank = Math.round((wcr * bankPct) / 100);
    }

    // ✅ Always exclude the first year
    return projectYears.map((_, yi) =>
      yi === 0 ? 0 : yi < firstPos ? 0 : roundedBank
    );
  }, [projectYears, computeValue, daysFor, totalCostRaw, bankPct]);

  // 12) Sundry Creditors by year
  const sundryCreditorByYear = useMemo(() => {
    return projectYears.map((_, yi) => {
      if (yi === 0) return 0;

      const days = daysFor["Sundry Creditors"] || 0;
      const closing = computeValue(
        "Raw Materials",
        daysFor["Raw Materials"],
        yi
      );
      const consumption = Number(totalCostRaw[yi]) || 0;
      const opening =
        yi > 0
          ? computeValue("Raw Materials", daysFor["Raw Materials"], yi - 1)
          : 0;
      const netPurchases = closing + consumption - opening;
      return (netPurchases / 365) * days;
    });
  }, [projectYears, computeValue, daysFor, totalCostRaw]);

  // 13) Sundry Debtors by year
  const sundryDebtorByYear = useMemo(() => {
    return projectYears.map((_, yi) => {
      if (yi === 0) return 0;

      const days = daysFor["Sundry Debtors"] || 0;
      const sales = Number(totalAnnualSales[yi]) || 0;
      const base = avgWorkingDays ? (sales / avgWorkingDays) * days : 0;
      const value = base + 10;
      const months = getMonthsActive(projectYears[yi]);
      return value * (months / 12);
    });
  }, [
    projectYears,
    daysFor,
    totalAnnualSales,
    avgWorkingDays,
    getMonthsActive,
  ]);

  // ────────────────────────────────────────────────────────────
  // 14) Persist all Working Capital data to API (once ready)
  // ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!projectId) return;
    if (loading || error) return;
    if (!projectYears.length) return;

    const payload = {
      workingCapitalRequirementData: workingCapitalRequirementByYear,
      rawMaterials: rawMaterialsByYear,
      finishedGoods: finishedGoodsByYear,
      workingCapitalBank: bankByYear,
      sundryCreditor: sundryCreditorByYear,
      sundryDebtors: sundryDebtorByYear,
    };

    API.put(`/user/project-inputs/${projectId}`, payload).catch((err) => {
      console.error("Failed to update working capital data on server:", err);
      setError("Could not save Working Capital data. Please retry.");
    });

    // optional: also keep a local backup if you want
    try {
      window.localStorage.setItem(
        "workingCapitalRawMaterials",
        JSON.stringify(rawMaterialsByYear)
      );
      window.localStorage.setItem(
        "workingCapitalFinishedGoods",
        JSON.stringify(finishedGoodsByYear)
      );
      window.localStorage.setItem(
        "workingCapitalRequirementData",
        JSON.stringify(workingCapitalRequirementByYear)
      );
    } catch {
      // ignore localStorage errors
    }
  }, [
    projectId,
    loading,
    error,
    projectYears,
    workingCapitalRequirementByYear,
    rawMaterialsByYear,
    finishedGoodsByYear,
    bankByYear,
    sundryCreditorByYear,
    sundryDebtorByYear,
  ]);

  // ────────────────────────────────────────────────────────────
  // 15) UI
  // ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-gray-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-center py-8">{error}</div>;
  }

  return (
    <div className="w-full p-6 space-y-8">
      <h1 className="text-2xl font-bold text-gray-800">
        Working Capital Requirement
      </h1>

      {/* ── Table 1: WCR Breakdown ───────────────────────────────── */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100">
            <tr className="h-10 text-gray-700">
              <th className="px-4 text-left">Particulars</th>
              <th className="px-4 text-left">Days</th>
              {projectYears.map((yr, i) => (
                <th key={i} className="px-4 text-center">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {wcrItems.map((r, idx) => (
              <React.Fragment key={idx}>
                <tr className={idx % 2 ? "bg-gray-50" : ""}>
                  <td className="px-4 py-2">{r.particular}</td>
                  <td className="px-4 py-2">{r.days}</td>
                  {projectYears.map((_, yi) => {
                    if (r.particular === "Sundry Creditors") {
                      const credValue = sundryCreditorByYear[yi] || 0;
                      return (
                        <td key={yi} className="px-4 py-2 text-right">
                          {credValue.toFixed(2)}
                        </td>
                      );
                    }
                    if (r.particular === "Sundry Debtors") {
                      const debtorValue = sundryDebtorByYear[yi] || 0;
                      return (
                        <td key={yi} className="px-4 py-2 text-right">
                          {debtorValue.toFixed(2)}
                        </td>
                      );
                    }
                    return (
                      <td key={yi} className="px-4 py-2 text-right">
                        {computeValue(r.particular, Number(r.days), yi).toFixed(
                          2
                        )}
                      </td>
                    );
                  })}
                </tr>
                {r.particular === "Sundry Debtors" && (
                  <tr className="bg-gray-200 font-semibold">
                    <td className="px-4 py-2 text-right">Total</td>
                    <td />
                    {projectYears.map((_, yi) => (
                      <td key={yi} className="px-4 py-2 text-right">
                        {(
                          (rawMaterialsByYear[yi] || 0) +
                          (finishedGoodsByYear[yi] || 0) +
                          (sundryDebtorByYear[yi] || 0)
                        ).toFixed(2)}
                      </td>
                    ))}
                  </tr>
                )}
              </React.Fragment>
            ))}

            <tr className="bg-green-100 font-semibold">
              <td className="px-4 py-2">Working Capital Requirement</td>
              <td />
              {workingCapitalRequirementByYear.map((wcr, yi) => (
                <td key={yi} className="px-4 py-2 text-right">
                  {wcr.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-blue-100 font-semibold">
              <td className="px-4 py-2">Working Capital (Bank)</td>
              <td />
              {bankByYear.map((val, i) => (
                <td key={i} className="px-4 py-2 text-right">
                  {val.toFixed(2)}
                </td>
              ))}
            </tr>
            <tr className="bg-yellow-100 font-semibold">
              <td className="px-4 py-2">Working Capital (Promoters)</td>
              <td />
              {(() => {
                const firstPos = projectYears.findIndex((_, yi) => {
                  const rmVal = computeValue(
                    "Raw Materials",
                    daysFor["Raw Materials"],
                    yi
                  );
                  const fgVal = computeValue(
                    "Finished Goods & WIP",
                    daysFor["Finished Goods & WIP"],
                    yi
                  );
                  const debtors = computeValue(
                    "Sundry Debtors",
                    daysFor["Sundry Debtors"],
                    yi
                  );
                  const total3 = rmVal + fgVal + debtors;

                  const days = daysFor["Sundry Creditors"] || 0;
                  const closing = rmVal;
                  const consumption = Number(totalCostRaw[yi]) || 0;
                  const opening =
                    yi > 0
                      ? computeValue(
                          "Raw Materials",
                          daysFor["Raw Materials"],
                          yi - 1
                        )
                      : 0;
                  const netPurchases = closing + consumption - opening;
                  const creditors = (netPurchases / 365) * days;
                  const wcr = total3 - creditors;
                  return wcr > 0;
                });

                let roundedBank = 0;
                if (firstPos >= 0) {
                  const yi = firstPos;
                  const rmVal = computeValue(
                    "Raw Materials",
                    daysFor["Raw Materials"],
                    yi
                  );
                  const fgVal = computeValue(
                    "Finished Goods & WIP",
                    daysFor["Finished Goods & WIP"],
                    yi
                  );
                  const debtors = computeValue(
                    "Sundry Debtors",
                    daysFor["Sundry Debtors"],
                    yi
                  );
                  const total3 = rmVal + fgVal + debtors;

                  const days = daysFor["Sundry Creditors"] || 0;
                  const closing = rmVal;
                  const consumption = Number(totalCostRaw[yi]) || 0;
                  const opening =
                    yi > 0
                      ? computeValue(
                          "Raw Materials",
                          daysFor["Raw Materials"],
                          yi - 1
                        )
                      : 0;
                  const netPurchases = closing + consumption - opening;
                  const creditors = (netPurchases / 365) * days;
                  const wcr = total3 - creditors;
                  roundedBank = Math.round((wcr * bankPct) / 100);
                }

                return projectYears.map((_, yi) => {
                  // ✅ Always exclude the first year
                  if (yi === 0) {
                    return (
                      <td key={yi} className="px-4 py-2 text-right">
                        {Number(0).toFixed(2)}
                      </td>
                    );
                  }

                  const rmVal = computeValue(
                    "Raw Materials",
                    daysFor["Raw Materials"],
                    yi
                  );
                  const fgVal = computeValue(
                    "Finished Goods & WIP",
                    daysFor["Finished Goods & WIP"],
                    yi
                  );
                  const debtors = computeValue(
                    "Sundry Debtors",
                    daysFor["Sundry Debtors"],
                    yi
                  );
                  const total3 = rmVal + fgVal + debtors;

                  const days = daysFor["Sundry Creditors"] || 0;
                  const closing = rmVal;
                  const consumption = Number(totalCostRaw[yi]) || 0;
                  const opening =
                    yi > 0
                      ? computeValue(
                          "Raw Materials",
                          daysFor["Raw Materials"],
                          yi - 1
                        )
                      : 0;
                  const netPurchases = closing + consumption - opening;
                  const creditors = (netPurchases / 365) * days;
                  const wcr = total3 - creditors;

                  const promoters = yi < firstPos ? 0 : wcr - roundedBank;
                  return (
                    <td key={yi} className="px-4 py-2 text-right">
                      {promoters.toFixed(2)}
                    </td>
                  );
                });
              })()}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ── Table 2: Closing Stock & Consumption ────────────────── */}
      <h2 className="text-xl font-semibold text-gray-700">
        Closing Stock & Consumption
      </h2>
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-100">
            <tr className="h-10 text-gray-700">
              <th className="px-4 text-left">Particulars</th>
              {projectYears.map((yr, i) => (
                <th key={i} className="px-4 text-center">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="px-4 py-2">Opening Stock (RM)</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-right">
                  {i === 0
                    ? "0.00"
                    : computeValue(
                        "Raw Materials",
                        daysFor["Raw Materials"],
                        i - 1
                      ).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2">Closing Stock (RM &amp; PM)</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-right">
                  {computeValue(
                    "Raw Materials",
                    daysFor["Raw Materials"],
                    i
                  ).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2">Consumption</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-right">
                  {(i === 0 ? 0 : Number(totalCostRaw[i]) || 0).toFixed(2)}
                </td>
              ))}
            </tr>
            <tr>
              <td className="px-4 py-2">Net Purchases</td>
              {projectYears.map((_, i) => {
                if (i === 0) {
                  return (
                    <td key={i} className="px-4 py-2 text-right">
                      {Number(0).toFixed(2)}
                    </td>
                  );
                }
                const closing = computeValue(
                  "Raw Materials",
                  daysFor["Raw Materials"],
                  i
                );
                const consumption = Number(totalCostRaw[i]) || 0;
                const opening =
                  i > 0
                    ? computeValue(
                        "Raw Materials",
                        daysFor["Raw Materials"],
                        i - 1
                      )
                    : 0;
                const np = closing + consumption - opening;
                return (
                  <td key={i} className="px-4 py-2 text-right">
                    {np.toFixed(2)}
                  </td>
                );
              })}
            </tr>
            <tr>
              <td className="px-4 py-2">
                Creditors of RM ({daysFor["Sundry Creditors"]} days)
              </td>
              {projectYears.map((_, i) => {
                if (i === 0) {
                  return (
                    <td key={i} className="px-4 py-2 text-right">
                      {Number(0).toFixed(2)}
                    </td>
                  );
                }
                const days = daysFor["Sundry Creditors"] || 0;
                const closing = computeValue(
                  "Raw Materials",
                  daysFor["Raw Materials"],
                  i
                );
                const consumption = Number(totalCostRaw[i]) || 0;
                const opening =
                  i > 0
                    ? computeValue(
                        "Raw Materials",
                        daysFor["Raw Materials"],
                        i - 1
                      )
                    : 0;
                const netPurchases = closing + consumption - opening;
                const cred = (netPurchases / 365) * days;
                return (
                  <td key={i} className="px-4 py-2 text-right">
                    {cred.toFixed(2)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
